/**
 * @author
 * David Rivera (jherax)
 * https://github.com/jherax
 */

import storage from 'proxy-storage';
import alterDate from './dates/alterDate';

/**
 * @private
 * Key of the namespace to store the experiment in the `localStorage`
 */
const key = 'hc-exp';

/**
 * @private
 * The value of the default namespace.
 *
 * @namespace {Object} `hc-exp`
 */
const ns = {};

/**
 * @private
 * Gets the object in the namespace.
 *
 * @return {Object}
 */
function getNamespace() {
  return storage.getItem(key) || ns;
}

/**
 * @private
 * Gets the data associated to a specific experiment.
 *
 * @param  {String} version: the name of the experiment in the namespace
 * @return {any}
 */
function getExperiment(version) {
  const expData = getNamespace()[version];
  if (expData) return expData.data;
  return expData;
}

/**
 * @private
 * Stores the data in a specific experiment.
 *
 * @param  {String} version: the name of the experiment in the namespace
 * @param  {any} value: the value associated to an experiment
 * @return {void}
 */
function setExperiment(version, value) {
  const namespace = getNamespace();
  const expData = {
    expires: alterDate({months: 1}).valueOf(),
    data: value,
  };
  namespace[version] = expData;
  storage.setItem(key, namespace);
}

/**
 * @private
 * Removes an specific experiment from the namespace.
 *
 * @param  {String} version: the name of the experiment in the namespace
 * @return {void}
 */
function removeExperiment(version) {
  const namespace = getNamespace();
  delete namespace[version];
  storage.setItem(key, namespace);
}

/**
 * @private
 * Removes all experiments in the namespace.
 *
 */
function clearExperiments() {
  storage.removeItem(key);
}

/**
 * @private
 * Checks if some of the experiments has expired.
 */
function checkExpiry() {
  let version,
    changed = false;
  const today = new Date().valueOf();
  const namespace = getNamespace();
  for (version in namespace) {
    if (today >= namespace[version].expires) {
      delete namespace[version];
      changed = true;
    }
  }
  if (changed) {
    storage.setItem(key, namespace);
  }
}

checkExpiry();

/**
 * @public
 * Manages data for experiments.
 *
 * @param  {String} command: name of the command to execute
 * @param  {String} version: the name of the experiment in the namespace
 * @param  {any} value: the value associated to an experiment
 * @throws {Error} when the command does not exists.
 * @return {any}
 */
export default function experiment(command, version, value) {
  switch (command) {
    case 'get':
      return getExperiment(version);
    case 'set':
      return setExperiment(version, value);
    case 'remove':
      return removeExperiment(version);
    case 'clear':
      return clearExperiments();
    default:
      throw new Error(`Command ${command} not exist`);
  }
}
